/**
 * Krok CRM2 — pomocné funkce nad tabulí úkolů v UI.
 *
 * ZADANÍ (důležité pro další údržbu): UI NEZNÁ životní cyklus úkolu. Ten
 * počítá API a posílá ho na kartě jako `allowedTransitions` (stejně jako
 * u poptávek v kroku CRM1 a zakázek v kroku R4). Tady jsou jen čtyři věci,
 * které automat neřeší: seskupení řádků do sloupců, text termínu, vyhledávání
 * a popisek zdroje.
 */

import { officeDayStart, officeNextDayStart } from '@reos/config';

/**
 * Sloupce tabule. Krok TAB1 bod 4 přidal `waiting` („Čeká na druhé“) —
 * výčet musí sedět na `TASK_STATUS_KEYS` v `packages/crm/src/internal/task-status.ts`
 * (hlídá to `task-board.spec.ts` proti sloupcům z API).
 */
export const TASK_STATUSES = ['todo', 'doing', 'waiting', 'done'] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Je hodnota jedním ze sloupců tabule? Server i UI musí znát stejný výčet. */
export function isTaskStatus(value: string): value is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(value);
}

/**
 * Je úkol OTEVŘENÝ (= pořád něčí práce)?
 *
 * JEDNO MÍSTO PRAVDY pro celý admin. Po TAB1 bod 4 se tahle otázka musí
 * odpovídat stejně na třech obrazovkách, které se jinak tiše rozejdou:
 * hlavička tabule („N otevřených“), odznak Úkolů v menu („N po termínu“) a
 * „co brzdí“ u zakázky (`case-next-step.ts` → `case-blocker.ts`).
 *
 * „Čeká na druhé“ je OTEVŘENÉ. Visí to na klientovi, úřadu nebo správci sítí,
 * ale hotové to není — a u „co brzdí“ je to ten NEJCENNĚJŠÍ případ: zakázka,
 * která stojí na někom jiném. Kdyby se `waiting` počítal jako zavřený, zmizela
 * by zakázce věta právě ve chvíli, kdy ji má nejvíc zapotřebí.
 *
 * Serverový protějšek je `isTaskDone` v `packages/crm/src/internal/task-status.ts`
 * (`isTaskOpen` je jeho negace). Zrcadlo je tu VĚDOMĚ: `internal/` jiného
 * kontextu se importovat nesmí (CLAUDE.md zlaté pravidlo 4). Že se obě strany
 * nerozešly, hlídá `task-board.spec.ts` proti výčtu sloupců z API.
 */
export function isTaskOpen(status: string): boolean {
  return status !== 'done';
}

/**
 * Krok TAB1 bod 4 — JEDNA FUNKCE PŘESUNU ÚKOLU.
 *
 * Zadání: „přechody stejnou funkcí jako dnešní drag a Dnešek (jedna funkce
 * přesunu)". Do TAB1 existovaly tři cesty, jak úkol posunout:
 *   1. tažení na tabuli (`routes/ukoly.tsx` → `transitionTask`),
 *   2. „Hotovo“ na Dnešku (`routes/dnesek.tsx` → `transitionTask('done')`),
 *   3. výběr sloupce v drawru (`components/task-drawer.tsx`).
 * Každá si sama rozhodovala, jestli cíl povolit — a jen jedna z nich
 * (tabule) `allowedTransitions` opravdu četla. Tohle je to rozhodnutí na
 * jednom místě; samotné volání API zůstává v `tasks-api.ts`.
 *
 * `allowedTransitions` počítá SERVER (`taskAllowedTransitions`), UI je jen
 * čte — stavový automat se v UI nedoplňuje (viz hlavička souboru).
 */
export interface TaskMoveTarget {
  readonly status: string;
  readonly allowedTransitions?: readonly string[] | undefined;
}

export type TaskMoveDecision =
  | { readonly ok: true; readonly to: TaskStatus }
  | { readonly ok: false; readonly reason: 'same_status' | 'not_allowed' | 'unknown_target' };

export function decideTaskMove(task: TaskMoveTarget, to: string): TaskMoveDecision {
  if (task.status === to) return { ok: false, reason: 'same_status' };
  if (!isTaskStatus(to)) return { ok: false, reason: 'unknown_target' };
  if (!(task.allowedTransitions ?? []).includes(to)) return { ok: false, reason: 'not_allowed' };
  return { ok: true, to };
}

export interface TaskColumnView {
  readonly key: string;
  readonly label: string;
  readonly position: number;
}

export interface TaskRow {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  readonly dueAt?: string | null | undefined;
  readonly ownerUserId?: string | null | undefined;
  readonly originKind?: string | null | undefined;
  readonly originLabel?: string | null | undefined;
  readonly description?: string | null | undefined;
  /** Krok TAB1 bod 4: kdy byl úkol odškrtnut — sloupec „Hotovo dnes“ to potřebuje. */
  readonly completedAt?: string | null | undefined;
}

export interface ColumnGroup<T> {
  readonly column: TaskColumnView;
  readonly items: readonly T[];
}

/**
 * Rozdělí úkoly do sloupců tabule. Sloupce jdou Z API — ne natvrdo — a úkol ve
 * sloupci, který v odpovědi není, NESMÍ zmizet (viz `groupByStage` u
 * poptávek): dostane vlastní šedý sloupec na konci, odkud se dá přetáhnout.
 */
export function groupTasksByColumn<T extends { readonly status: string }>(
  rows: readonly T[],
  columns: readonly TaskColumnView[],
): readonly ColumnGroup<T>[] {
  const ordered = [...columns].sort((a, b) => a.position - b.position || a.key.localeCompare(b.key));
  const buckets = new Map<string, T[]>(ordered.map((column) => [column.key, []]));
  const orphans = new Map<string, T[]>();
  for (const row of rows) {
    const bucket = buckets.get(row.status);
    if (bucket !== undefined) {
      bucket.push(row);
    } else {
      const list = orphans.get(row.status);
      if (list === undefined) orphans.set(row.status, [row]);
      else list.push(row);
    }
  }
  const groups: ColumnGroup<T>[] = ordered.map((column) => ({
    column,
    items: buckets.get(column.key) ?? [],
  }));
  for (const [key, items] of orphans) {
    groups.push({
      column: { key, label: key, position: 9000 + groups.length },
      items,
    });
  }
  return groups;
}

/**
 * Krok UXP4a bod 5 (nalez P2-18): hranice dne je `Europe/Prague` a je JEDNA
 * pro celý systém — `officeDayStart` z `@reos/config`. Tady byla do tohoto
 * kroku TŘETÍ kopie („poledník UTC", ROZHODNUTÍ CRM2-4), takže tabule Úkolů
 * mohla mezi 00:00 a 02:00 pražského času ukazovat jiný „termín dnes" než
 * Dnešek a než server.
 */

export type DueKind = 'none' | 'overdue' | 'today' | 'tomorrow' | 'later';

/** Zařazení termínu do dne, ve kterém se karta zobrazuje. */
export function dueKind(dueAt: string | null | undefined, now: Date): DueKind {
  if (typeof dueAt !== 'string' || dueAt === '') return 'none';
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return 'none';
  const start = officeDayStart(now).getTime();
  if (due < start) return 'overdue';
  /*
   * Krok NOC3 bod 3 (nalez N20): hranice ZITRKA je `officeNextDayStart`, ne
   * „start zitrka + 86 400 000". Soucet s pevnym dnem ukazoval na zacatek
   * POZITRI, takze cokoli do konce zitrka vyslo jako „dnes" a tabule rikala
   * cervene „Termín dnes" u ukolu splatneho zitra. Pozitri se pocita znovu
   * pres `officeNextDayStart` (ne +24 h) kvuli zmene casu — 25. 10. 2026 ma
   * pražsky den 25 hodin.
   */
  const tomorrowStart = officeNextDayStart(now).getTime();
  if (due < tomorrowStart) return 'today';
  if (due < officeNextDayStart(new Date(tomorrowStart)).getTime()) return 'tomorrow';
  return 'later';
}

const DAY_MS = 86_400_000;

/** Dny do termínu; záporné = po termínu, `null` = termín není. */
export function daysUntilDue(dueAt: string | null | undefined, now: Date): number | null {
  if (typeof dueAt !== 'string' || dueAt === '') return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  return Math.round((officeDayStart(new Date(due)).getTime() - officeDayStart(now).getTime()) / DAY_MS);
}

/** Formát data pro UI (den v měsíci bez roku — rok v kartě není potřeba). */
export function formatDueDate(dueAt: string | null | undefined): string {
  if (typeof dueAt !== 'string' || dueAt === '') return '';
  const date = new Date(dueAt);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric' }).format(date);
}

/** `DateTimeField` drzi `YYYY-MM-DDTHH:mm` bez zóny — musí se při uložení doplnit (krok UXF3). */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Opačný směr: hodnota z `<input type="datetime-local">` na ISO pro API. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (value.trim() === '') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** Hledání v kartě: název, poznámka a popisek zdroje (číslo faktury, kód brány). */
export function matchesTaskQuery(row: TaskRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  const haystack = [row.title, row.originLabel ?? '', row.description ?? '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/** Který zdroj automatiky karta nese; `null` = ruční úkol. */
export function taskOriginKey(originKind: string | null | undefined): string | null {
  return typeof originKind === 'string' && originKind !== '' ? originKind : null;
}

/* ------------------------------------------------------------------ TAB1 */

/**
 * Krok TAB1 bod 3 — ČIPY „Vše / Automatické / Ruční“ (předloha `Ukoly.png`).
 * Čip „E-mail“ v předloze patří schránce makléře a dělá ho krok AS1; dokud
 * schránka připojená není, čip se nevykresluje (zadání: „žádný prázdný
 * falešný sloupec, žádná demo karta e-mailu“).
 */
export const TASK_SOURCE_FILTERS = ['all', 'auto', 'manual'] as const;
export type TaskSourceFilter = (typeof TASK_SOURCE_FILTERS)[number];

export function matchesTaskSource(row: TaskRow, filter: TaskSourceFilter): boolean {
  if (filter === 'all') return true;
  const auto = taskOriginKey(row.originKind) !== null;
  return filter === 'auto' ? auto : !auto;
}

/**
 * Krok TAB1 bod 2 — „N otevřených · N po termínu“ v hlavičce.
 *
 * „Otevřený“ = cokoli, co není `done` (tedy i „Čeká na druhé“: pořád je to
 * nedokončená věc, jen na ní makléř zrovna nehraje). „Po termínu“ je TOTÁŽ
 * definice jako na Dnešku — hranice dne je poledník UTC (ROZHODNUTÍ CRM2-4).
 */
export function openTaskCount(rows: readonly { readonly status: string }[]): number {
  return rows.filter((row) => isTaskOpen(row.status)).length;
}

export function overdueTaskCount(
  rows: readonly { readonly status: string; readonly dueAt?: string | null | undefined }[],
  now: Date,
): number {
  return rows.filter((row) => isTaskOpen(row.status) && dueKind(row.dueAt, now) === 'overdue').length;
}

/**
 * Krok TAB1 bod 4 — SLOUPEC „HOTOVO DNES“.
 *
 * Předloha ukazuje jen to, co je hotové DNES; starší hotové jsou za odkazem
 * „Starší hotové“. Bez toho sloupec po měsíci provozu přeroste obrazovku a
 * přestane být zprávou o dnešku.
 *
 * Den se bere podle `completedAt` (kdy to makléř odškrtl) a hranicí je
 * POLEDNÍK UTC — tatáž hranice jako u termínů (ROZHODNUTÍ CRM2-4). Místní
 * půlnoc by znamenala, že „dnes“ na tabuli a „dnes“ v Dnešku jsou dvě různé
 * věci podle toho, v jaké zóně běží prohlížeč a v jaké server.
 */
export function isDoneToday(
  row: { readonly status: string; readonly completedAt?: string | null | undefined },
  now: Date,
): boolean {
  if (row.status !== 'done') return false;
  const completedAt = row.completedAt ?? null;
  if (completedAt === null || completedAt === '') return false;
  const at = new Date(completedAt);
  if (Number.isNaN(at.getTime())) return false;
  const start = officeDayStart(now).getTime();
  return at.getTime() >= start && at.getTime() < officeNextDayStart(now).getTime();
}

